import { useQueryClient } from 'react-query';
import Entri from 'entrijs';
import { ENTRI_CONFIG } from '../constants/queryData';
import { getEntriAuthToken } from '../api';
import { timestamp } from '../utils/dateTime';
import { refreshToken } from './useEntriConnect';
import { palette } from '../theme';
import config from '../config';
import useUser from './useUser';
import { NONE } from '../constants/blogTypes';

const { SHOWIT_STATIC_IP } = config;

/**
 * Hook to handle Entri Sell domain purchasing functionality
 * @returns {{
 *  purchaseDomain: () => Promise<void>,
 * }}
 */
const useEntriSell = () => {
  const queryClient = useQueryClient();
  const { user } = useUser();

  const dnsRecords =
    user.blogType === NONE
      ? [
          {
            type: 'A',
            host: '@',
            value: SHOWIT_STATIC_IP,
            ttl: 3600,
          },
          {
            type: 'A',
            host: '*',
            value: SHOWIT_STATIC_IP,
            ttl: 3600,
          },
          {
            type: 'CNAME',
            host: 'www',
            value: `{DOMAIN}`,
            ttl: 3600,
          },
        ]
      : [];

  /**
   * Opens the Entri Sell modal for domain purchase
   */
  const purchaseDomain = async () => {
    try {
      // Get Entri token
      const { token, expires, appId } = await queryClient.fetchQuery(
        [ENTRI_CONFIG],
        getEntriAuthToken,
        {
          staleTime: Infinity,
        }
      );

      // Check if token is expired
      const isExpired = timestamp() >= expires;
      const currentToken = isExpired ? await refreshToken(queryClient) : token;

      // Launch Entri purchaseDomain modal
      const entri = await Entri.load({
        applicationId: appId,
        token: currentToken,
      });
      entri.purchaseDomain({
        sellVersion: 'v3',
        applicationId: appId,
        token: currentToken,
        dnsRecords,
        successCallbackUrl: config.SUCCESS_CALLBACK_URL,
        exitCallbackUrl: config.EXIT_CALLBACK_URL,
        freeDomain: true,
        disableMailUpsell: true,
        whiteLabel: {
          sell: {
            partnerLogo: 'https://showit.com/wp-content/uploads/2017/03/Showit-Logo-White.svg',
            partnerIcon: 'https://showit.com/wp-content/uploads/2017/03/Showit-Icon-Black.svg',
            contact: {
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.emailAddress,
            },
          },
          customCopy: {
            initialScreen: {
              title: {
                en: 'Find Your Perfect Domain Name',
                es: 'Encuentra Tu Nombre de Dominio Perfecto',
                fr: 'Trouvez Votre Nom de Domaine Parfait',
                de: 'Finden Sie Ihren Perfekten Domainnamen',
              },
              marketingCopy: {
                en: 'Secure a unique domain name to establish your brand online and reach customers worldwide.',
                es: 'Asegura un nombre de dominio único para establecer tu marca en línea y llegar a clientes en todo el mundo.',
                fr: 'Sécurisez un nom de domaine unique pour établir votre marque en ligne et atteindre des clients dans le monde entier.',
                de: 'Sichern Sie sich einen einzigartigen Domainnamen, um Ihre Marke online zu etablieren und Kunden weltweit zu erreichen.',
              },
            },
          },
          theme: {
            primary: palette.black,
            onPrimary: palette.primary.contrastText,
            secondary: palette.secondary.main,
            onSecondary: palette.secondary.contrastText,
            hideEntriLogo: true,
            activating: palette.black,
            onActivating: palette.primary.contrastText,
          },
        },
        debugMode: config.TEST,
      });
    } catch (error) {
      console.error('Error launching Entri Sell:', error);
    }
  };

  return { purchaseDomain };
};

export default useEntriSell;
